// GENERATED from messaging-platform/packages/chat-core/src/people-mentions.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
/**
 * @-mentions of people (2026-09-21), the counterpart of `#` for patients (patient-mentions.ts): typing `@` in the
 * composer offers the people who may be mentioned in that conversation (the hub's `conv:mentionable`), picking one
 * writes "@Their Name" into the sentence, and the message is sent with their principal id in `mentions`. The hub keeps
 * only ids it allows (who can already read the conversation), brings a mentioned teammate into it, and pushes to them
 * even when their notify level is "mentions only".
 *
 * Framework-free, like the rest of chat-core: the apps draw the picker and the highlighted names.
 */

/** Someone the caller may @-mention in one conversation. */
export interface MentionablePerson {
  principalId: string;
  name: string;
  avatar?: string;
  /** their clinical user id, given to a clinical caller, so an @mention can become a task's assignee */
  clinicalUserId?: string;
}

/** An @-mention in a draft: the person, and the exact text it put there (a mention whose text is gone is dropped). */
export interface DraftPersonMention {
  principalId: string;
  name: string;
  text: string;
}

/** One piece of a message body: plain text, or an @-mention of one person. */
export interface BodySegment {
  text: string;
  /** set on an @-mention: whose */
  principalId?: string;
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** The text a mention of `name` writes into the sentence. */
export function mentionText(name: string): string {
  return `@${name.trim()}`;
}

/**
 * People whose name matches `query`: every word of the query must start a word of the name ("jo sm" finds "Dr John
 * Smith"), best first (a match on the first name word, then shorter names), without the ones in `exclude`.
 */
export function searchPeople(
  people: readonly MentionablePerson[],
  query: string,
  options: { limit?: number; exclude?: ReadonlySet<string> } = {},
): MentionablePerson[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  const limit = options.limit ?? 6;
  const scored: { p: MentionablePerson; score: number }[] = [];
  for (const p of people) {
    if (options.exclude?.has(p.principalId)) continue;
    const nameWords = fold(p.name).split(/[\s.,'-]+/).filter(Boolean);
    if (!words.every((w) => nameWords.some((n) => n.startsWith(w)))) continue;
    // Titles ("dr", "prof") are skipped when deciding what counts as the first word of a name.
    const first = nameWords.find((n) => !TITLES.has(n)) ?? nameWords[0] ?? '';
    const score = (words.length && first.startsWith(words[0]!) ? 0 : 1) * 1000 + p.name.length;
    scored.push({ p, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.p.name.localeCompare(b.p.name))
    .slice(0, limit)
    .map((x) => x.p);
}

const TITLES = new Set(['dr', 'prof', 'a/prof', 'mr', 'mrs', 'ms', 'miss', 'sr', 'sister']);

/**
 * The `@query` the caret is in, or null: `@` at the start or after a space, then up to four words on one line. Used
 * to open, narrow and close the picker as the user types.
 */
export function atQueryBefore(textBeforeCaret: string): { query: string; start: number } | null {
  const m = /(?:^|\s)@([^@\n]{0,40})$/.exec(textBeforeCaret);
  if (!m) return null;
  const query = m[1] ?? '';
  if (query.startsWith(' ') || query.split(/\s+/).length > 4) return null;
  return { query, start: textBeforeCaret.length - query.length - 1 };
}

/** The draft's @-mentions whose text is still in it, one per person: the ids to send. */
export function activePeopleMentions(text: string, mentions: readonly DraftPersonMention[]): string[] {
  const seen = new Set<string>();
  return mentions.filter((m) => text.includes(m.text) && !seen.has(m.principalId) && seen.add(m.principalId)).map((m) => m.principalId);
}

/**
 * Where the draft's @-mentions sit in `text` (every occurrence of each one's text): the patient suggestions skip these
 * spans (detectPatients' `skipSpans`), so a colleague's name that happens to match a patient is never offered as one.
 */
export function peopleMentionSpans(text: string, mentions: readonly Pick<DraftPersonMention, 'text'>[]): { start: number; end: number }[] {
  const spans: { start: number; end: number }[] = [];
  for (const m of mentions) {
    if (!m.text) continue;
    for (let at = text.indexOf(m.text); at >= 0; at = text.indexOf(m.text, at + 1)) spans.push({ start: at, end: at + m.text.length });
  }
  return spans;
}

/**
 * A message body split for display, with each "@Name" of a MENTIONED person marked (a name typed with @ but not
 * picked, or of someone who is not mentioned, stays plain text). Longer names are matched first, so "@Anna Smith-Jones"
 * is not cut at "@Anna Smith".
 */
export function mentionSegments(body: string, mentioned: readonly { principalId: string; name: string }[]): BodySegment[] {
  const named = mentioned.filter((m) => m.name.trim()).map((m) => ({ id: m.principalId, text: mentionText(m.name) }));
  if (!body || !named.length) return [{ text: body }];
  named.sort((a, b) => b.text.length - a.text.length);
  const out: BodySegment[] = [];
  let plain = '';
  let i = 0;
  while (i < body.length) {
    const hit = body[i] === '@' && (i === 0 || /\s|\(/.test(body[i - 1]!))
      ? named.find((n) => body.startsWith(n.text, i) && !/[\p{L}\p{N}]/u.test(body[i + n.text.length] ?? ''))
      : undefined;
    if (hit) {
      if (plain) out.push({ text: plain });
      plain = '';
      out.push({ text: hit.text, principalId: hit.id });
      i += hit.text.length;
    } else {
      plain += body[i];
      i += 1;
    }
  }
  if (plain) out.push({ text: plain });
  return out;
}

/**
 * The composer's @ Colleague and # Patient buttons: the trigger goes after what is typed, with a space first unless the
 * text is empty or already ends in a space or a new line, so the picker opens as if it had been typed.
 */
export function withTrigger(text: string, trigger: '@' | '#'): string {
  return !text || /\s$/.test(text) ? `${text}${trigger}` : `${text} ${trigger}`;
}
